/**
 * Advertisement placement component for mobile-first design
 */

import React from 'react';
import { AdConfig } from './adConfig';

export interface AdPlacementProps {
  config: AdConfig;
  position: 'inline' | 'footer';
  index?: number;
  className?: string;
}

// Mock ad content for demonstration
const adContent = [
  {
    id: 1,
    title: "Fresh Organic Vegetables",
    description: "Get the best organic vegetables delivered to your door",
    image: "https://images.unsplash.com/photo-1550989460-0adf9ea622e2?w=400&h=200&fit=crop",
    url: "#",
    sponsor: "Local Harvest Co."
  },
  {
    id: 2,
    title: "Farm Fresh Dairy",
    description: "Pure milk and cheese from local dairy farms",
    image: "https://images.unsplash.com/photo-1628088062854-d1870b4553da?w=400&h=200&fit=crop",
    url: "#",
    sponsor: "Valley Dairy"
  },
  {
    id: 3,
    title: "Seasonal Fruit Boxes",
    description: "Subscribe to weekly seasonal fruit deliveries",
    image: "https://images.unsplash.com/photo-1610832958506-aa56368176cf?w=400&h=200&fit=crop",
    url: "#",
    sponsor: "Orchard Fresh"
  }
];

export default function AdPlacement({ config, position, index, className = '' }: AdPlacementProps) {
  // Don't render if ads are disabled
  if (!config.enabled) {
    return null;
  }

  // For inline ads, check if this index should show an ad
  if (position === 'inline' && typeof index === 'number') {
    const shouldShowAd = (index + 1) % config.frequency === 0;
    if (!shouldShowAd) {
      return null;
    }
  }

  // For footer ads, check if footer ads are enabled
  if (position === 'footer' && !config.footerEnabled) {
    return null;
  }

  // Get a random ad for variety
  const ad = adContent[Math.floor(Math.random() * adContent.length)];

  const baseClasses = "bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg border border-teal-200 overflow-hidden shadow-sm";
  const fullClasses = `${baseClasses} ${className}`;

  if (position === 'footer') {
    return (
      <div className={`${fullClasses} p-4 md:p-6`}>
        <div className="flex flex-col md:flex-row items-center gap-4">
          <img
            src={ad.image}
            alt={ad.title}
            className="w-full md:w-32 h-24 md:h-20 object-cover rounded"
          />
          <div className="flex-grow text-center md:text-left">
            <div className="text-xs text-secondary mb-1 font-medium">
              Sponsored by {ad.sponsor}
            </div>
            <h3 className="font-semibold text-primary-dark mb-1">{ad.title}</h3>
            <p className="text-sm text-gray-600 mb-2">{ad.description}</p>
            <a
              href={ad.url}
              className="inline-block bg-primary text-white px-4 py-2 rounded text-sm hover:bg-primary-dark transition-colors"
              onClick={(e) => {
                e.preventDefault();
                // Ad click tracked - don't log for privacy
              }}
            >
              Learn More
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Inline ad format (smaller, card-like)
  return (
    <div className={`${fullClasses} p-3`}>
      <div className="text-xs text-secondary mb-1 font-medium text-center">
        Sponsored
      </div>
      <div className="flex gap-3">
        <img
          src={ad.image}
          alt={ad.title}
          className="w-16 h-16 object-cover rounded flex-shrink-0"
        />
        <div className="flex-grow min-w-0">
          <h4 className="font-medium text-sm text-primary-dark mb-1 truncate">
            {ad.title}
          </h4>
          <p className="text-xs text-gray-600 mb-2 line-clamp-2">
            {ad.description}
          </p>
          <a
            href={ad.url}
            className="inline-block bg-primary text-white px-3 py-1 rounded text-xs hover:bg-primary-dark transition-colors"
            onClick={(e) => {
              e.preventDefault();
              // Inline ad click tracked - don't log for privacy
            }}
          >
            View
          </a>
        </div>
      </div>
    </div>
  );
}